/**
 * Affiliate (influencer) service — stateless orchestration for the control
 * panel and the affiliate portal. Screens call this and never touch
 * `affiliateApi` or `apiClient`.
 *
 * The list endpoint pages by `startIndex` (a row offset) and reports
 * `totalRecords`, so this converts to and from the page numbers the UI uses.
 *
 * Referral-code and profile reads accept an optional `auth`: the portal passes
 * the affiliate's own bearer, the control panel omits it and runs on the
 * operator's member session.
 */
import { UnusableResponseError } from '../../utils/apiErrorHandler';
import { isPagedEnvelope } from '../admin/adminMapper';
import { createInvalidOtpError } from '../auth/authErrors';
import affiliateApi, { type AffiliateAuth } from './affiliateApi';
import {
  buildAffiliateEarningsSummary,
  toAffiliateEarningsRangePayload,
} from './affiliateEarnings';
import AFFILIATE_ENDPOINTS from './affiliateEndpoints';
import {
  createAffiliateNotApprovedError,
  createAffiliateRequestError,
} from './affiliateErrors';
import type {
  Affiliate,
  AffiliateEarningsFilter,
  AffiliateEarningsSummary,
  AffiliateMutationResponse,
  AffiliatePasswordReset,
  AffiliatePayout,
  AffiliateProfile,
  AffiliateReferralCode,
  AffiliateReferralCodeResponse,
  AffiliateReviewState,
  AffiliateSession,
  AffiliateUserDetailResponse,
  AffiliateUserResponse,
} from './affiliateTypes';

const DEFAULT_PAGE_SIZE = 20;

/**
 * `GetAffiliateReferralCodes` pages now. Neither screen that reads it paginates
 * — both show every code an affiliate owns beside the totals — so one page wide
 * enough for that is requested and `totalCount` is returned so a caller can tell
 * when even this was not enough.
 */
const REFERRAL_CODE_PAGE_SIZE = 100;

const text = (value: string | null | undefined): string => value ?? '';

/** The earnings roll-ups are newer than some deployments; treat a gap as zero. */
const amount = (value: number | null | undefined): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * -1 is the API's "awaiting review" sentinel. Any other unrecognised value
 * falls through to the same answer: showing an unreviewed application as a
 * decision would be worse than showing it as still pending.
 */
const reviewState = (isActive: number | null | undefined): AffiliateReviewState => {
  if (isActive === 1) {
    return 'approved';
  }
  if (isActive === 0) {
    return 'rejected';
  }
  return 'pending';
};

const mapAffiliate = (item: AffiliateUserResponse): Affiliate => ({
  affiliateUserId: item.affiliateUserId,
  email: text(item.email),
  address: [text(item.address1), text(item.address2)].filter(Boolean).join(', '),
  country: text(item.country),
  howDidYouHear: text(item.howDidYouHear),
  state: reviewState(item.isActive),
  createdDate: text(item.createdDate),
  reviewedDate: text(item.reviewedDate),
  usageCount: amount(item.usageCount),
  totalEarnings: amount(item.totalEarnings),
});

const mapReferralCode = (
  item: AffiliateReferralCodeResponse,
): AffiliateReferralCode => ({
  id: item.affiliateReferralCodeId,
  affiliateUserId: item.affiliateUserId,
  code: text(item.referralCode),
  description: text(item.description),
  commissionPerDownload: item.commissionPerDownload ?? 0,
  isActive: item.isActive === true,
  createdDate: text(item.createdDate),
  usageCount: amount(item.usageCount),
  totalEarnings: amount(item.totalEarnings),
});

const mapProfile = (item: AffiliateUserDetailResponse): AffiliateProfile => ({
  affiliateUserId: item.affiliateUserId,
  email: text(item.email),
  password: text(item.password),
  address1: text(item.address1),
  address2: text(item.address2),
  country: text(item.country),
  howDidYouHear: text(item.howDidYouHear),
  state: reviewState(item.isActive),
});

/**
 * The affiliate auth endpoints answer 200 with a failing `status`/`statusCode`
 * instead of an HTTP error, so `apiClient` treats a refusal as a success. Both
 * shapes are normalised here rather than in a screen.
 */
const rejectedByStatus = (payload: {
  status?: string | null;
  statusCode?: number;
}): boolean => {
  if (typeof payload.statusCode === 'number' && payload.statusCode >= 400) {
    return true;
  }
  return typeof payload.status === 'string'
    ? payload.status.toLowerCase() !== 'success'
    : false;
};

/**
 * Normalizes the referral-code mutation envelope. These endpoints can return
 * the contradictory combination `success: false` with a successful embedded
 * status code and success message after persisting the mutation. The embedded
 * status code is authoritative; `success` remains the fallback when the code
 * is absent.
 */
const assertMutationAccepted = (
  data: AffiliateMutationResponse | undefined,
  endpoint: string,
): void => {
  if (!data) {
    throw new UnusableResponseError(endpoint);
  }
  // The status *code* only: the documented envelope has no `status` string, so
  // an undocumented one ("Created") must not be read as a refusal.
  const successfulStatus =
    typeof data.statusCode === 'number' &&
    data.statusCode >= 200 &&
    data.statusCode < 300;
  if (
    data.statusCode >= 400 ||
    (!successfulStatus && data.success === false)
  ) {
    throw createAffiliateRequestError(data.message);
  }
};

export interface AffiliateListParams {
  searchKeyword?: string;
  /** 1 approved, 0 rejected, null every application. */
  isActiveFilter?: number | null;
  pageNumber?: number;
  pageSize?: number;
  /** Only the interval is sent: this endpoint has no payment-state filter. */
  earningsFilter?: AffiliateEarningsFilter;
}

export interface AffiliatePage {
  items: Affiliate[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  /** Roll-up across the rows on this page, not across the whole result set. */
  earnings: AffiliateEarningsSummary;
}

/**
 * `GetAffiliateReferralCodes` reports the payment roll-up beside the rows, so
 * this is the only read that can answer what an affiliate has been paid and is
 * still owed.
 */
export interface AffiliateReferralCodeList {
  codes: AffiliateReferralCode[];
  earnings: AffiliateEarningsSummary;
  /** Codes the API holds, which is more than `codes` once one page is not enough. */
  totalCount: number;
}

export interface AffiliateReferralCodeParams {
  auth?: AffiliateAuth;
  earningsFilter?: AffiliateEarningsFilter;
}

const affiliateService = {
  getAffiliates: async (
    params: AffiliateListParams = {},
  ): Promise<AffiliatePage> => {
    const pageNumber = params.pageNumber ?? 1;
    const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
    const range = params.earningsFilter
      ? toAffiliateEarningsRangePayload(params.earningsFilter)
      : { startDate: null, endDate: null };
    const { data } = await affiliateApi.list({
      searchKeyword: params.searchKeyword ? params.searchKeyword : null,
      isActiveFilter: params.isActiveFilter ?? null,
      startDate: range.startDate,
      endDate: range.endDate,
      startIndex: (pageNumber - 1) * pageSize,
      pageSize,
    });

    // `affiliates` is nullable in the contract, so an empty page is valid.
    if (!isPagedEnvelope(data, 'affiliates')) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.LIST);
    }

    const totalCount = data.totalRecords ?? 0;
    const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
    const items = (Array.isArray(data.affiliates) ? data.affiliates : []).map(
      mapAffiliate,
    );
    return {
      items,
      totalCount,
      pageNumber,
      pageSize,
      totalPages,
      hasNextPage: pageNumber < totalPages,
      hasPreviousPage: pageNumber > 1,
      // No payment figures: `AffiliateUserResponse` carries earnings and
      // redemptions only, so the breakdown is read per affiliate instead.
      earnings: buildAffiliateEarningsSummary({
        totalEarnings: items.reduce((sum, row) => sum + row.totalEarnings, 0),
        usageCount: items.reduce((sum, row) => sum + row.usageCount, 0),
      }),
    };
  },

  /**
   * AdminReviewAffiliate is the one affiliate operation whose 200 body carries
   * no `success` flag, so `apiClient` cannot reject it. A refusal arrives as
   * 200 with a failing `statusCode`/`status`, which must be turned into an
   * error here or the operator is told a rejected review succeeded.
   */
  reviewAffiliate: async (
    affiliateUserId: number,
    approve: boolean,
    actionBy: number,
  ): Promise<void> => {
    const { data } = await affiliateApi.review({
      affiliateUserId,
      isActive: approve ? 1 : 0,
      actionBy,
    });
    const statusCode = data?.statusCode;
    if (typeof statusCode === 'number' && statusCode >= 400) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.ADMIN_REVIEW);
    }
  },

  /**
   * Soft-deletes an affiliate account; the API deactivates that affiliate's
   * referral codes with it, so every code they own stops crediting downloads.
   *
   * Like AdminReviewAffiliate the 200 body carries no `success` flag, so the
   * status *code* is the only guard. Deliberately not `rejectedByStatus`: this
   * envelope's `status` string is undocumented in value, and reading an
   * accepted "Deleted" as a refusal would tell the operator the account is
   * still there and invite them to delete it again.
   */
  deleteAffiliate: async (affiliateUserId: number): Promise<void> => {
    const { data } = await affiliateApi.deleteAffiliate({ affiliateUserId });
    if (!data) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.DELETE);
    }
    if ((data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data.message);
    }
  },

  /**
   * Signs the affiliate in. A refusal arrives as 200 with no token, so the body
   * is normalised into a rejection; a valid password on an application still
   * awaiting (or refused) review is rejected separately so the portal can say
   * why instead of claiming the credentials were wrong.
   */
  login: async (email: string, password: string): Promise<AffiliateSession> => {
    const { data } = await affiliateApi.login({ email, password });
    if (!data || rejectedByStatus(data) || !data.token) {
      throw createAffiliateRequestError(data?.message);
    }

    const info = data.affiliateInfo;
    const state = reviewState(info?.isActive ?? data.isActive);
    if (state !== 'approved') {
      throw createAffiliateNotApprovedError(state === 'rejected');
    }

    const affiliateUserId = info?.affiliateUserId ?? data.affiliateUserId;
    if (!affiliateUserId) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.VALIDATE_LOGIN);
    }

    return {
      token: data.token,
      affiliateUserId,
      email: text(info?.email) || email,
      state,
    };
  },

  /**
   * Starts a password reset and returns the token the OTP step needs.
   *
   * The API answers 200 with a token for an address that has no affiliate
   * account, deliberately, so this cannot report whether the email is
   * registered — an unknown address simply never receives a code.
   */
  forgotPassword: async (email: string): Promise<AffiliatePasswordReset> => {
    const { data } = await affiliateApi.forgotPassword({ email });
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
    if (!data.passwordResetToken) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.FORGOT_PASSWORD);
    }
    return { passwordResetToken: data.passwordResetToken };
  },

  /**
   * Verifies the emailed code. A wrong one arrives as 200 `{ isValid: false }`,
   * so it is turned into the same invalid-OTP rejection the member flow raises
   * and the screen can tell it apart from a transport failure.
   */
  verifyPasswordResetOtp: async (payload: {
    passwordResetToken: string;
    otpCode: string;
  }): Promise<void> => {
    const { data } = await affiliateApi.verifyPasswordResetOtp({
      passwordResetToken: payload.passwordResetToken,
      otpCode: payload.otpCode,
    });
    if (data?.isValid === false) {
      throw createInvalidOtpError(data.message ?? undefined);
    }
    // Only an explicit `false` counts as a wrong code; anything else that is
    // not a success is a request failure.
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
  },

  /** Replays the verified OTP with the new password; the token alone is not enough. */
  resetPassword: async (payload: {
    email: string;
    passwordResetToken: string;
    otpCode: string;
    newPassword: string;
  }): Promise<void> => {
    const { data } = await affiliateApi.resetPassword({
      email: payload.email,
      passwordResetToken: payload.passwordResetToken,
      otpCode: payload.otpCode,
      newPassword: payload.newPassword,
    });
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
  },

  /**
   * Files an affiliate application. Approval is a separate operator step, so a
   * success here does not grant a session — the portal sends the applicant to
   * sign-in and the account stays `pending` until reviewed.
   */
  register: async (payload: {
    email: string;
    password: string;
    address1: string;
    address2?: string;
    countryId: number;
    howDidYouHear: string;
  }): Promise<void> => {
    const { data } = await affiliateApi.register({
      email: payload.email,
      password: payload.password,
      address1: payload.address1,
      address2: payload.address2 ? payload.address2 : null,
      countryId: payload.countryId,
      howDidYouHear: payload.howDidYouHear,
    });
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
  },

  getProfile: async (
    affiliateUserId: number,
    auth?: AffiliateAuth,
  ): Promise<AffiliateProfile> => {
    const { data } = await affiliateApi.getById({ affiliateUserId }, auth);
    if (!data || rejectedByStatus(data) || !data.user) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.BY_ID);
    }
    return mapProfile(data.user);
  },

  /**
   * `password` is mandatory on this endpoint, so an edit that does not change it
   * must resend the stored one — the caller passes the value read back from
   * `getProfile`.
   */
  updateProfile: async (
    payload: {
      affiliateUserId: number;
      email: string;
      password: string;
      address1: string;
      address2?: string;
      countryId: number;
      howDidYouHear: string;
    },
    auth?: AffiliateAuth,
  ): Promise<AffiliateProfile> => {
    const { data } = await affiliateApi.updateProfile(
      {
        affiliateUserId: payload.affiliateUserId,
        email: payload.email,
        password: payload.password,
        address1: payload.address1,
        address2: payload.address2 ? payload.address2 : null,
        countryId: payload.countryId,
        howDidYouHear: payload.howDidYouHear,
      },
      auth,
    );
    if (!data || rejectedByStatus(data)) {
      throw createAffiliateRequestError(data?.message);
    }
    if (!data.user) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.UPDATE_PROFILE);
    }
    return mapProfile(data.user);
  },

  getReferralCodes: async (
    affiliateUserId: number,
    params: AffiliateReferralCodeParams = {},
  ): Promise<AffiliateReferralCodeList> => {
    const range = params.earningsFilter
      ? toAffiliateEarningsRangePayload(params.earningsFilter)
      : { startDate: null, endDate: null };
    const { data } = await affiliateApi.referralCodes(
      {
        affiliateUserId,
        startDate: range.startDate,
        endDate: range.endDate,
        startIndex: 0,
        pageSize: REFERRAL_CODE_PAGE_SIZE,
      },
      params.auth,
    );
    // An affiliate with no codes yet is a normal state, not a failure.
    if (!data || (!data.success && !Array.isArray(data.referralCodes))) {
      throw new UnusableResponseError(AFFILIATE_ENDPOINTS.REFERRAL_CODES);
    }
    const codes = (Array.isArray(data.referralCodes) ? data.referralCodes : []).map(
      mapReferralCode,
    );
    return {
      codes,
      // A genuine 0 is kept; only a deployment that omits the field falls back
      // to the row count, so a caller comparing the two can spot truncation.
      totalCount:
        typeof data.totalRecords === 'number' && Number.isFinite(data.totalRecords)
          ? data.totalRecords
          : codes.length,
      earnings: buildAffiliateEarningsSummary(
        {
          // The envelope total covers every code, including any the page omits.
          totalEarnings: amount(data.totalEarnings),
          usageCount: codes.reduce((sum, code) => sum + code.usageCount, 0),
        },
        {
          paidEarnings: data.totalPaid,
          dueEarnings: data.unpaidBalance,
          unclaimedEarnings: data.unclaimed,
          lifetimeEarnings: data.lifetimeEarned,
          lifetimePaidEarnings: data.lifetimePaid,
        },
      ),
    };
  },

  createReferralCode: async (
    payload: {
      affiliateUserId: number;
      referralCode: string;
      description?: string;
      commissionPerDownload: number;
    },
    auth?: AffiliateAuth,
  ): Promise<void> => {
    const { data } = await affiliateApi.createReferralCode(
      {
        affiliateUserId: payload.affiliateUserId,
        referralCode: payload.referralCode,
        description: payload.description ?? null,
        commissionPerDownload: payload.commissionPerDownload,
        isActive: true,
      },
      auth,
    );
    assertMutationAccepted(data, AFFILIATE_ENDPOINTS.CREATE_REFERRAL_CODE);
  },

  updateReferralCode: async (
    payload: {
      affiliateReferralCodeId: number;
      affiliateUserId: number;
      referralCode: string;
      description?: string;
    },
    auth?: AffiliateAuth,
  ): Promise<void> => {
    const { data } = await affiliateApi.updateReferralCode(
      {
        affiliateReferralCodeId: payload.affiliateReferralCodeId,
        affiliateUserId: payload.affiliateUserId,
        referralCode: payload.referralCode,
        description: payload.description ?? null,
      },
      auth,
    );
    assertMutationAccepted(data, AFFILIATE_ENDPOINTS.UPDATE_REFERRAL_CODE);
  },

  deleteReferralCode: async (
    affiliateReferralCodeId: number,
    affiliateUserId: number,
    auth?: AffiliateAuth,
  ): Promise<void> => {
    const { data } = await affiliateApi.deleteReferralCode(
      {
        affiliateReferralCodeId,
        affiliateUserId,
      },
      auth,
    );
    assertMutationAccepted(data, AFFILIATE_ENDPOINTS.DELETE_REFERRAL_CODE);
  },

  /**
   * Submits a PayPal payout batch for one affiliate. PayPal settles
   * asynchronously, so the answer only says the batch was accepted; its final
   * state arrives on the API's webhook, which is why the returned status is
   * surfaced verbatim rather than translated into "paid".
   *
   * A refusal is recognised from `success` (which `apiClient` already rejects on)
   * and from the status *code* only. Deliberately not `rejectedByStatus`: that
   * treats any `status` string other than "success" as a failure, and this
   * envelope's `status` describes an asynchronous batch, so a perfectly accepted
   * `"PENDING"` would be reported to the operator as a failed payout — inviting
   * them to send the money a second time.
   */
  sendPayout: async (payload: {
    affiliateUserId: number;
    influencerEmail: string;
    amount: number;
    note?: string;
  }): Promise<AffiliatePayout> => {
    const { data } = await affiliateApi.sendPayout({
      affiliateUserId: payload.affiliateUserId,
      influencerEmail: payload.influencerEmail,
      amount: payload.amount,
      note: payload.note ? payload.note : null,
    });
    if (!data || data.success === false || (data.statusCode ?? 0) >= 400) {
      throw createAffiliateRequestError(data?.message);
    }
    return {
      batchId: text(data.batchId),
      batchStatus: text(data.batchStatus),
      message: text(data.message),
    };
  },
};

export default affiliateService;
